// @flow

import React, { PureComponent } from 'react';
import request from 'superagent';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import UpdateCompanyMutation from 'main-app/mutations/UpdateCompany';

const Container = styled.div`
  padding: 24px;
  max-width: 600px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Description = styled.p`
  font-size: 13px;
  color: #666;
  margin-bottom: 24px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 12px;
  background: #fff;
`;

const ToggleLabel = styled.div``;

const ToggleLabelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
`;

const ToggleLabelDesc = styled.div`
  font-size: 12px;
  color: #888;
`;

const Toggle = styled.input`
  width: 38px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
`;

type State = {
  loading: boolean,
  autoEmailOnOrderCreated: boolean,
  autoEmailOnInvoiceCreated: boolean,
  autoEmailOnShipmentCreated: boolean,
  saving: boolean,
};

class EmailNotifications extends PureComponent<{}, State> {
  state = {
    loading: true,
    autoEmailOnOrderCreated: false,
    autoEmailOnInvoiceCreated: false,
    autoEmailOnShipmentCreated: false,
    saving: false,
  };

  async componentDidMount() {
    try {
      const res = await request.post('/graphql').send({
        query: `{ viewer { company { id autoEmailOnOrderCreated autoEmailOnInvoiceCreated autoEmailOnShipmentCreated } } }`,
      });
      const { company } = res.body.data.viewer;
      this.setState({
        loading: false,
        autoEmailOnOrderCreated: company.autoEmailOnOrderCreated,
        autoEmailOnInvoiceCreated: company.autoEmailOnInvoiceCreated,
        autoEmailOnShipmentCreated: company.autoEmailOnShipmentCreated,
      });
    } catch (e) {
      this.setState({ loading: false });
      Actions.alertNotification(e.message, 'error');
    }
  }

  handleSave = async () => {
    const {
      autoEmailOnOrderCreated,
      autoEmailOnInvoiceCreated,
      autoEmailOnShipmentCreated,
    } = this.state;

    this.setState({ saving: true });

    try {
      await UpdateCompanyMutation.commit({
        variables: {
          input: {
            autoEmailOnOrderCreated,
            autoEmailOnInvoiceCreated,
            autoEmailOnShipmentCreated,
          },
        },
      });
      Actions.alertNotification(
        i18n.t('Email notification preferences saved.'),
        'success',
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }

    this.setState({ saving: false });
  };

  render() {
    const {
      loading,
      autoEmailOnOrderCreated,
      autoEmailOnInvoiceCreated,
      autoEmailOnShipmentCreated,
      saving,
    } = this.state;

    if (loading) return <Loader />;

    return (
      <Container>
        <Title>{i18n.t('Automated Email Notifications')}</Title>
        <Description>
          {i18n.t(
            'Automatically send email notifications to customers when key events occur. Manual email buttons are unaffected by these settings.',
          )}
        </Description>

        <ToggleRow>
          <ToggleLabel>
            <ToggleLabelTitle>
              {i18n.t('Sales Order Created')}
            </ToggleLabelTitle>
            <ToggleLabelDesc>
              {i18n.t(
                'Send an order confirmation email to the customer when a new sales order is created.',
              )}
            </ToggleLabelDesc>
          </ToggleLabel>
          <Toggle
            type="checkbox"
            checked={!!autoEmailOnOrderCreated}
            onChange={e =>
              this.setState({ autoEmailOnOrderCreated: e.target.checked })
            }
          />
        </ToggleRow>

        <ToggleRow>
          <ToggleLabel>
            <ToggleLabelTitle>
              {i18n.t('Invoice Created')}
            </ToggleLabelTitle>
            <ToggleLabelDesc>
              {i18n.t(
                'Send an invoice email to the customer when a new invoice is created.',
              )}
            </ToggleLabelDesc>
          </ToggleLabel>
          <Toggle
            type="checkbox"
            checked={!!autoEmailOnInvoiceCreated}
            onChange={e =>
              this.setState({ autoEmailOnInvoiceCreated: e.target.checked })
            }
          />
        </ToggleRow>

        <ToggleRow>
          <ToggleLabel>
            <ToggleLabelTitle>
              {i18n.t('Shipment Created')}
            </ToggleLabelTitle>
            <ToggleLabelDesc>
              {i18n.t(
                'Send a shipping confirmation email to the customer when a new shipment is created.',
              )}
            </ToggleLabelDesc>
          </ToggleLabel>
          <Toggle
            type="checkbox"
            checked={!!autoEmailOnShipmentCreated}
            onChange={e =>
              this.setState({ autoEmailOnShipmentCreated: e.target.checked })
            }
          />
        </ToggleRow>

        <Button
          theme="blue"
          loading={saving}
          onClick={this.handleSave}
          style={{ marginTop: 8 }}
        >
          {i18n.t('Save Preferences')}
        </Button>
      </Container>
    );
  }
}

export default EmailNotifications;
